<template>
  <component
    :is="guessedTagName"
    :id="id"
    ref="componentRef"
    :href="href"
    :autofocus="autofocus"
    :disabled="disabled"
    :value="value"
    :name="name"
    :type="type"
    @click="clickHandler"
    @focus="focusHandler"
    @blur="blurHandler"
    @keydown="keydownHandler"
    @mousedown="mousedownHandler"
  >
    <slot>{{ text || field?.value }}</slot>
  </component>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';

defineOptions({ name: 'ButtonFi' });

interface Props {
  value?: string | number | null;
  tagName?: 'button' | 'a';
  type?: string | undefined;
  href?: string | null;
  text?: string | undefined;
  id?: string | undefined;
  name?: string | undefined;
  disabled?: boolean;
  autofocus?: boolean;
  field?: { value?: string } | null;
  naturalEvents?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  value: null,
  tagName: 'button',
  href: null,
  field: null,
  type: undefined,
  text: undefined,
  id: undefined,
  name: undefined,
});

// Do not include 'click' — it must remain a native event so hrefs are followed
const emit = defineEmits<{
  blur: [e: FocusEvent];
  focus: [e: FocusEvent];
  keydown: [e: KeyboardEvent];
  mousedown: [e: MouseEvent];
  'natural-click': [e: MouseEvent];
}>();

const guessedTagName = computed(() => {
  if (props.href != null && !props.disabled) {
    return 'a';
  }
  return props.tagName;
});

const clickHandler = (e: MouseEvent) => {
  if (props.naturalEvents) {
    emit('natural-click', e);
    return;
  }
  e.preventDefault();
};

const focusHandler = (e: FocusEvent) => {
  emit('focus', e);
};

const blurHandler = (e: FocusEvent) => {
  emit('blur', e);
};

const keydownHandler = (e: KeyboardEvent) => {
  emit('keydown', e);
};

const mousedownHandler = (e: MouseEvent) => {
  emit('mousedown', e);
};

const el = useTemplateRef<HTMLButtonElement | HTMLAnchorElement>('componentRef');

const blur = () => {
  el.value?.blur();
};

const focus = () => {
  el.value?.focus();
};

defineExpose({ blur, focus });
</script>

<style src="./button-fi-unscoped" />
